import CloseRounded from "@mui/icons-material/CloseRounded";
import ExitToAppOutlined from "@mui/icons-material/ExitToAppOutlined";
import PictureInPictureOutlined from "@mui/icons-material/PictureInPictureOutlined";
import {
  Box,
  Divider,
  Fade,
  Paper,
  Portal,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { isFunction } from "lodash";
import Flx from "../layout/Flx";
import Htag from "../typography/Htag";
import Txt from "../typography/Txt";
import { C } from "../closingReadiness/closingReadinessUtils";
// The shell look (paper, resize grip, header buttons) is shared with the
// calendar's ClosingDetailDrawer — see panelChrome.js; never restyle it here.
import {
  PANEL_DEFAULT_WIDTH,
  PanelIconButton,
  panelFloatingPaperSx,
  panelPaperSx,
  resizeHandleSx,
  resizeThumbSx,
} from "./shared/panelChrome";
const BODY_BACKGROUND_GRAY = "#f5f6fa";

// Keep the popped-out card at least this far from every viewport edge so it can
// never be dragged/resized off-screen.
const EDGE_MARGIN = 8;
const POP_MIN_WIDTH = 360;
const POP_MIN_HEIGHT = 280;
// Default popped-out height as a fraction of the viewport (the "pinned" size).
// Tall by design — leaves only a small, roughly-equal top/bottom margin.
const POP_DEFAULT_HEIGHT_FACTOR = 0.92;

const clampNum = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// Floating-card resize hit zones — thin strips on each edge plus larger corner
// squares, inside the card's border (the Paper clips overflow). Order matters:
// corners come after edges so they sit on top where the two overlap.
const POP_EDGE = 6;
const POP_CORNER = 16;
const POP_RESIZE_CURSORS = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
  nw: "nwse-resize",
  se: "nwse-resize",
};
const POP_RESIZE_ZONES = [
  { dir: "n", sx: { top: 0, left: POP_CORNER, right: POP_CORNER, height: POP_EDGE } },
  { dir: "s", sx: { bottom: 0, left: POP_CORNER, right: POP_CORNER, height: POP_EDGE } },
  { dir: "w", sx: { left: 0, top: POP_CORNER, bottom: POP_CORNER, width: POP_EDGE } },
  { dir: "e", sx: { right: 0, top: POP_CORNER, bottom: POP_CORNER, width: POP_EDGE } },
  { dir: "nw", sx: { top: 0, left: 0, width: POP_CORNER, height: POP_CORNER } },
  { dir: "ne", sx: { top: 0, right: 0, width: POP_CORNER, height: POP_CORNER } },
  { dir: "sw", sx: { bottom: 0, left: 0, width: POP_CORNER, height: POP_CORNER } },
  { dir: "se", sx: { bottom: 0, right: 0, width: 20, height: 20 } },
];

// Clamp a restored pop-out SIZE to the current viewport. Height is also capped
// at the standard pinned height so a previously oversized card never reopens
// taller than the default (and never off-screen).
const clampPopSizeToViewport = (size, fallbackW) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxH = Math.min(
    vh - 2 * EDGE_MARGIN,
    Math.round(vh * POP_DEFAULT_HEIGHT_FACTOR),
  );
  return {
    w: clampNum(
      size?.w ?? fallbackW ?? POP_MIN_WIDTH,
      POP_MIN_WIDTH,
      Math.max(POP_MIN_WIDTH, vw - 2 * EDGE_MARGIN),
    ),
    h: clampNum(size?.h ?? POP_MIN_HEIGHT, POP_MIN_HEIGHT, maxH),
  };
};

// Clamp a restored pop-out POSITION so the card (given its clamped size) sits
// fully within the viewport.
const clampPopPosToViewport = (pos, size) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxX = Math.max(EDGE_MARGIN, vw - size.w - EDGE_MARGIN);
  const maxY = Math.max(EDGE_MARGIN, vh - size.h - EDGE_MARGIN);
  return {
    x: clampNum(pos?.x ?? EDGE_MARGIN, EDGE_MARGIN, maxX),
    y: clampNum(pos?.y ?? EDGE_MARGIN, EDGE_MARGIN, maxY),
  };
};

// Session-scoped memory of each panel's pop-out layout (keyed by `persistKey`),
// so the floating/docked state survives remounts (e.g. switching drilldown tabs).
// Module-level on purpose: it persists across mounts but resets on a full reload.
const POP_STATE_STORE = new Map();

/**
 * ResizeableSidebar - Stateless sidebar shell component
 * Handles the sidebar UI, resizing, animations, and backdrop
 * Content is managed by ResizeableSidebarContent
 *
 * Pop-out: a "Pop out" button next to the close button detaches the panel into a
 * free-floating card (like the loan-chat popover) that the user can drag by its
 * header and resize from any edge or corner. Position + size are clamped to
 * the viewport so nothing ever overflows the page. Pressing the button again
 * docks it back to the right edge. Disable entirely with `disablePopOut`.
 */
const ResizeableSidebar = ({
  title,
  titleIcon,
  description,
  // Put the description beside the title (centred on it, wrapping under it
  // when the panel is narrow) instead of on its own line below.
  descriptionInline = false,
  descriptionContent,
  children,
  onClose,
  open = false,
  hideDivider = false,
  hideBackdrop = false,
  sx,
  closeButtonStartContent,
  headerEndContent,
  bodyPadding = 2,
  defaultWidth = PANEL_DEFAULT_WIDTH,
  // Upper bound for the docked drag-resize. No default — a panel can be dragged
  // out to the full viewport width unless a caller explicitly caps it (e.g. a
  // viewer that only reads well up to a certain width).
  maxWidth: maxWidthProp,
  bodyPy,
  bodyPx,
  pxSize = 2,
  backgroundColorGray = false,
  headerBottomContent,
  headerSx,
  bodySx,
  // Pinned bottom bar — a SIBLING of the scrolling body, not part of it, so a
  // form's action row never moves and the body is the only thing that scrolls
  // (FORM_STYLE_GUIDE.md §12b). Omitted → no footer, exactly as before.
  footerContent,
  disablePopOut = false,
  // Which screen edge the docked panel pins to. Defaults to the right edge;
  // pass "left" to dock on the left (e.g. a file preview opened while a
  // right-docked drawer is already open, so the two sit side by side).
  anchor = "right",
  // When set, the pop-out state (floating vs docked + position/size) is
  // remembered under this key for the session, so it survives remounts — e.g.
  // switching drilldown tabs keeps the panel popped out. A different key (a
  // different loan) starts fresh.
  persistKey,
  // Hold the body at its pre-drag size while the panel is being resized and
  // lay it out once on release. For bodies that are expensive to re-layout
  // (the Excel preview's 1,000-row table with sticky headers): re-laying them
  // on every mousemove makes the edge lag the cursor and jump.
  freezeBodyWhileResizing = false,
}) => {
  const dockLeft = anchor === "left";
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [width, setWidth] = useState(isMobile ? "100%" : defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  const bodyRef = useRef(null);
  // Body size captured at drag start (freezeBodyWhileResizing only).
  const [frozenBody, setFrozenBody] = useState(null);
  const freezeBody = () => {
    if (!freezeBodyWhileResizing || !bodyRef.current) return;
    const { width: w, height: h } = bodyRef.current.getBoundingClientRect();
    setFrozenBody({ w, h });
  };
  const minWidth = 320;
  // Numeric cap for the drag handler — only when a caller passed one. Mobile
  // renders full-width anyway, so there's nothing to clamp there.
  const maxWidth = isMobile ? null : Number(maxWidthProp) || null;

  // ── Pop-out (floating, draggable + resizable card) state ──────────────
  // Seed from the session store when a persistKey is provided so the popped-out
  // layout survives a remount (e.g. changing drilldown tabs). The restored
  // size/position are clamped to the CURRENT viewport at mount so a layout
  // saved on a larger window (or a previously oversized resize) can never
  // reopen off-screen.
  const seed = persistKey ? POP_STATE_STORE.get(persistKey) : undefined;
  const seedSize = seed?.popSize
    ? clampPopSizeToViewport(seed.popSize, defaultWidth)
    : null;
  const seedPos =
    seed?.popPos && seedSize
      ? clampPopPosToViewport(seed.popPos, seedSize)
      : null;
  const [poppedOut, setPoppedOut] = useState(seed?.poppedOut ?? false);
  const [popPos, setPopPos] = useState(seedPos ?? { x: 0, y: 0 });
  const [popSize, setPopSize] = useState(
    seedSize ?? { w: defaultWidth, h: 560 },
  );
  const dragRef = useRef(null);
  const popResizeRef = useRef(null);
  // Active edge/corner while resizing the floating card (drives the shield).
  const [popResizeDir, setPopResizeDir] = useState(null);

  // Persist the pop-out layout so it's restored on the next mount under this key.
  useEffect(() => {
    if (!persistKey) return;
    POP_STATE_STORE.set(persistKey, { poppedOut, popPos, popSize });
  }, [persistKey, poppedOut, popPos, popSize]);

  // Keep the floating card within the viewport given its size.
  const clampPos = (x, y, w, h) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxX = Math.max(EDGE_MARGIN, vw - w - EDGE_MARGIN);
    const maxY = Math.max(EDGE_MARGIN, vh - h - EDGE_MARGIN);
    return {
      x: clampNum(x, EDGE_MARGIN, maxX),
      y: clampNum(y, EDGE_MARGIN, maxY),
    };
  };

  // const pxSize = isMobile ? 2.4 : 3.4;
  // Handle mouse move for resizing (docked left-edge handle only)
  useEffect(() => {
    // Coalesce to one width update per frame: mousemove can fire several times
    // a frame, and each update re-lays out the panel.
    let frame = null;
    let pending = null;
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      // Right-docked grows as the cursor moves left; left-docked grows as it
      // moves right. Clamp rather than ignore out-of-range moves, so a fast
      // drag past a limit still lands ON the limit instead of short of it.
      const raw = dockLeft ? e.clientX : window.innerWidth - e.clientX;
      pending = clampNum(raw, minWidth, maxWidth || Infinity);
      if (frame == null) {
        frame = requestAnimationFrame(() => {
          frame = null;
          setWidth(pending);
        });
      }
    };

    const handleMouseUp = () => {
      if (frame != null) {
        cancelAnimationFrame(frame);
        frame = null;
        if (pending != null) setWidth(pending);
      }
      setIsResizing(false);
      setFrozenBody(null);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      if (frame != null) cancelAnimationFrame(frame);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, maxWidth, dockLeft]);

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && open) {
        if (isFunction(onClose)) {
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, dispatch]);

  // Re-clamp the floating card on viewport resize so a shrinking window can
  // never strand it (or part of it) off-screen.
  useEffect(() => {
    if (!poppedOut) return undefined;
    const onResize = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = Math.min(popSize.w, vw - 2 * EDGE_MARGIN);
      const h = Math.min(popSize.h, vh - 2 * EDGE_MARGIN);
      setPopSize({ w, h });
      setPopPos((p) => clampPos(p.x, p.y, w, h));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [poppedOut, popSize.w, popSize.h]);

  const handleClose = () => {
    if (isFunction(onClose)) {
      onClose();
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    freezeBody();
    setIsResizing(true);
  };

  // ── Pop out / dock back ───────────────────────────────────────────────
  const handlePopOut = () => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(
      typeof width === "number" ? width : defaultWidth,
      vw - 2 * EDGE_MARGIN,
    );
    const h = Math.min(
      Math.round(vh * POP_DEFAULT_HEIGHT_FACTOR),
      vh - 2 * EDGE_MARGIN,
    );
    // Open near the right edge (where the docked panel was) and vertically
    // centered so the small top/bottom margins are roughly equal.
    setPopSize({ w, h });
    setPopPos(clampPos(vw - w - 32, Math.round((vh - h) / 2), w, h));
    setPoppedOut(true);
  };
  const handleDock = () => setPoppedOut(false);

  // ── Drag the card by its header ───────────────────────────────────────
  const handleHeaderPointerDown = (e) => {
    if (!poppedOut) return;
    // Don't start a drag from an interactive control inside the header.
    if (e.target.closest?.("button, .MuiIconButton-root, a, input, textarea"))
      return;
    if (e.button != null && e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: popPos.x,
      baseY: popPos.y,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handleHeaderPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const nx = d.baseX + (e.clientX - d.startX);
    const ny = d.baseY + (e.clientY - d.startY);
    setPopPos(clampPos(nx, ny, popSize.w, popSize.h));
  };
  const handleHeaderPointerUp = (e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // ── Resize the card from any edge or corner ───────────────────────────
  // `dir` is a compass string ("n", "se", "w", …). The edge(s) named move with
  // the pointer; the opposite edge(s) stay anchored, like a desktop window.
  const handlePopResizeDown = (dir) => (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    popResizeRef.current = {
      dir,
      startX: e.clientX,
      startY: e.clientY,
      baseX: popPos.x,
      baseY: popPos.y,
      baseW: popSize.w,
      baseH: popSize.h,
    };
    freezeBody();
    setPopResizeDir(dir);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handlePopResizeMove = (e) => {
    const r = popResizeRef.current;
    if (!r) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dx = e.clientX - r.startX;
    const dy = e.clientY - r.startY;
    let { baseX: x, baseY: y, baseW: w, baseH: h } = r;

    if (r.dir.includes("e")) {
      w = clampNum(r.baseW + dx, POP_MIN_WIDTH, vw - r.baseX - EDGE_MARGIN);
    } else if (r.dir.includes("w")) {
      const right = r.baseX + r.baseW;
      w = clampNum(r.baseW - dx, POP_MIN_WIDTH, right - EDGE_MARGIN);
      x = right - w;
    }
    if (r.dir.includes("s")) {
      h = clampNum(r.baseH + dy, POP_MIN_HEIGHT, vh - r.baseY - EDGE_MARGIN);
    } else if (r.dir.includes("n")) {
      const bottom = r.baseY + r.baseH;
      h = clampNum(r.baseH - dy, POP_MIN_HEIGHT, bottom - EDGE_MARGIN);
      y = bottom - h;
    }
    setPopSize({ w, h });
    setPopPos({ x, y });
  };
  const handlePopResizeUp = (e) => {
    if (!popResizeRef.current) return;
    popResizeRef.current = null;
    setPopResizeDir(null);
    setFrozenBody(null);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  if (!open) return null;

  const containerSx = poppedOut
    ? {
        position: "fixed",
        top: popPos.y,
        left: popPos.x,
        width: `${popSize.w}px`,
        height: `${popSize.h}px`,
        zIndex: 13000,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "12px",
        ...panelFloatingPaperSx,
        ...sx,
      }
    : {
        position: "fixed",
        top: 0,
        ...(dockLeft ? { left: 0 } : { right: 0 }),
        bottom: 0,
        width: `${width}px`,
        zIndex: 13000,
        display: "flex",
        // Square corners while docked — same as the calendar drawer's paper.
        borderRadius: 0,
        flexDirection: "column",
        overflow: "hidden",
        ...panelPaperSx(anchor),
        ...sx,
      };

  // Portaled to <body>: the panel is position:fixed at z 13000, but an ancestor
  // with its own stacking context (a transformed layout wrapper, a MUI Dialog
  // whose portal sits later in <body>) would otherwise be able to paint over it.
  // Rendering at the body root guarantees it opens OVER any open modal.
  return (
    <Portal>
      {/* Drag shield — while resizing the docked panel, a transparent full-screen
          overlay sits above everything (incl. the file-preview iframe) so the
          pointer never enters the iframe. Without it, moving the cursor over the
          iframe routes mousemove to the iframe's document and the parent's
          mousemove listener stops firing, freezing the resize. */}
      {(isResizing || popResizeDir) && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 20001,
            cursor: popResizeDir
              ? POP_RESIZE_CURSORS[popResizeDir]
              : "ew-resize",
          }}
        />
      )}

      {/* Backdrop — no dimming scrim, same as the calendar drawer: the page
          stays readable beside the panel and a click on it still closes.
          Hidden while popped out so the page stays interactive. */}
      {hideBackdrop || poppedOut ? null : (
        <Box
          onClick={handleClose}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "transparent",
            zIndex: 1200,
          }}
        />
      )}

      {/* Sidebar / floating card — surface, edge line and shadow come from the
          shared panel chrome (elevation 0 so MUI's paper shadow doesn't stack). */}
      <Fade in={open}>
        <Paper ref={sidebarRef} elevation={0} sx={containerSx}>
          {/* Docked left-edge resize handle (hidden when floating) */}
          {!poppedOut && (
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                position: "absolute",
                // Handle sits on the inner edge: left panel resizes from its
                // right side, right panel from its left side.
                ...(dockLeft ? { right: 0 } : { left: 0 }),
                top: 0,
                bottom: 0,
                zIndex: 20000,
                ...resizeHandleSx(isResizing),
              }}
            >
              <Box className="resize-thumb" sx={resizeThumbSx(isResizing)} />
            </Box>
          )}

          {/* Header — doubles as the drag handle while popped out */}
          <Box
            onPointerDown={handleHeaderPointerDown}
            onPointerMove={handleHeaderPointerMove}
            onPointerUp={handleHeaderPointerUp}
            sx={{
              p: 2,
              gap: 1,
              px: pxSize,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              flexShrink: 0,
              cursor: poppedOut ? "move" : "default",
              touchAction: poppedOut ? "none" : "auto",
              userSelect: poppedOut ? "none" : "auto",
              ...headerSx,
            }}
          >
            <Flx
              column={!descriptionInline}
              sx={{
                flex: 1,
                minWidth: 0,
                ...(descriptionInline && {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "2px 8px",
                }),
              }}
            >
              <Flx ac g={1} sx={{ minWidth: 0 }}>
                {titleIcon}
                <Htag
                  h1
                  bold
                  fontSize="20px"
                  sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {title}
                </Htag>
              </Flx>
              {description && (
                <Txt
                  sx={{
                    fontSize: "12px",
                    color: grey[600],
                    mt: descriptionInline ? 0 : 0.5,
                  }}
                >
                  {description}
                </Txt>
              )}
              {descriptionContent && descriptionContent}
            </Flx>
            <Flx
              ac
              sx={{
                flexShrink: 0,
                width: "auto",
                // Center, not flex-start: the close icon is taller than the
                // pop-out/action icons, so top-aligning leaves anything passed
                // as headerEndContent sitting a few px above the rest of the row.
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              {headerEndContent}
              {/* Every header control is a PanelIconButton (30px square) — the
                  extras a caller passes in closeButtonStartContent included. */}
              <Flx ac g={0.5}>
                {closeButtonStartContent}
                {!disablePopOut && (
                  <PanelIconButton
                    title={poppedOut ? "Dock to side" : "Pop out"}
                    icon={poppedOut ? ExitToAppOutlined : PictureInPictureOutlined}
                    onClick={poppedOut ? handleDock : handlePopOut}
                  />
                )}
                <PanelIconButton
                  title="Close panel"
                  icon={CloseRounded}
                  onClick={handleClose}
                />
              </Flx>
            </Flx>
          </Box>
          {headerBottomContent}
          {hideDivider ? null : <Divider sx={{ borderColor: C.line }} />}
          {/* Content */}
          <Box
            ref={bodyRef}
            sx={{
              flexGrow: 1,
              height: "100%",
              minHeight: 0,
              px: bodyPx || pxSize,
              py: bodyPy || bodyPadding,
              p: bodyPadding,
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor: backgroundColorGray
                ? BODY_BACKGROUND_GRAY
                : "#ffffff",
              ...bodySx,
              // Pinned to the pre-drag size until release (the Paper clips it).
              ...(frozenBody && {
                width: `${frozenBody.w}px`,
                height: `${frozenBody.h}px`,
                flexGrow: 0,
                flexShrink: 0,
              }),
            }}
          >
            {children}
          </Box>

          {/* Pinned footer — outside the scroller, so it never moves. */}
          {footerContent ? (
            <Box sx={{ flexShrink: 0 }}>{footerContent}</Box>
          ) : null}

          {/* Resize hit zones on every edge + corner (floating mode only).
              Invisible strips along the card's border with the matching
              resize cursor; the bottom-right corner keeps a visible grip as
              the discoverable affordance. Corners render last so they win
              where they overlap the edge strips. */}
          {poppedOut &&
            POP_RESIZE_ZONES.map(({ dir, sx: zoneSx }) => (
              <Box
                key={dir}
                onPointerDown={handlePopResizeDown(dir)}
                onPointerMove={handlePopResizeMove}
                onPointerUp={handlePopResizeUp}
                onLostPointerCapture={handlePopResizeUp}
                sx={{
                  position: "absolute",
                  cursor: POP_RESIZE_CURSORS[dir],
                  touchAction: "none",
                  zIndex: 20001,
                  ...zoneSx,
                  ...(dir === "se" && {
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: 4,
                      bottom: 4,
                      width: 9,
                      height: 9,
                      borderRight: `2px solid ${grey[400]}`,
                      borderBottom: `2px solid ${grey[400]}`,
                    },
                    "&:hover::after": {
                      borderColor: theme.palette.primary.main,
                    },
                  }),
                }}
              />
            ))}
        </Paper>
      </Fade>
    </Portal>
  );
};

export default ResizeableSidebar;
